import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  QrCode, Users, Shield, Star, ArrowRight, Building2,
  Smartphone, BarChart3, FileSpreadsheet, Search, UserCheck, Clock, Mail, Briefcase, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const featuresRef = useRef(null);
  const howItWorksRef = useRef(null);
  const [customPages, setCustomPages] = useState([]);
  const [pagesEnabled, setPagesEnabled] = useState({ about: true, contact: true, faq: true });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-up').forEach((el) => observer.observe(el));

    // Fetch custom pages for footer
    axios.get(`${API}/custom-pages`).then(res => {
      setCustomPages(res.data || []);
    }).catch(() => {});

    // Fetch page visibility settings
    axios.get(`${API}/site-settings`).then(res => {
      if (res.data?.pages_enabled) setPagesEnabled(res.data.pages_enabled);
    }).catch(() => {});

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="landing-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Manrope:wght@400;500;600;700&display=swap');
        .fade-up {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .fade-up.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .fade-up:nth-child(2) { transition-delay: 0.1s; }
        .fade-up:nth-child(3) { transition-delay: 0.2s; }
        .fade-up:nth-child(4) { transition-delay: 0.3s; }
        .gold-shimmer {
          background: linear-gradient(45deg, #D4A373 0%, #E6C6A0 50%, #D4A373 100%);
          background-size: 200% 200%;
          animation: shimmer 3s ease infinite;
        }
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      {/* ─── NAV ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-stone-200/60" data-testid="landing-nav">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <Building2 className="text-[#1A4D2E]" size={28} />
            <span
              className="text-lg sm:text-xl font-bold text-[#1A4D2E] truncate max-w-[160px] sm:max-w-none"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              <span className="sm:hidden">RE Lead Network</span>
              <span className="hidden sm:inline">Real Estate Lead & Referral Network</span>
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 px-3 sm:px-4"
              onClick={() => navigate('/find-jobs')}
              data-testid="nav-find-jobs-btn"
            >
              <Briefcase size={18} className="sm:mr-2" />
              <span className="hidden sm:inline">Find Jobs</span>
            </Button>
            <Button
              variant="ghost"
              className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 px-3 sm:px-4"
              onClick={() => navigate('/search')}
              data-testid="nav-search-btn"
            >
              <Search size={18} className="sm:mr-2" />
              <span className="hidden sm:inline">Find Professionals</span>
            </Button>
            <Button
              variant="ghost"
              className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 px-3 sm:px-4"
              onClick={() => navigate('/connect')}
              data-testid="nav-connect-btn"
            >
              <MessageSquare size={18} className="sm:mr-2" />
              <span className="hidden sm:inline">Albriks Connect</span>
            </Button>
            <Button
              className="bg-[#1A4D2E] text-white hover:bg-[#143D24] rounded-sm px-4 sm:px-5 py-2 font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
            >
              Log In
            </Button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBzdW5saXR8ZW58MHx8fHwxNzczMTk1MTI0fDA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, #1A4D2E 0%, rgba(26,77,46,0.6) 40%, rgba(26,77,46,0.1) 100%)',
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-16 md:pb-24 w-full">
          <p
            className="text-[#D4A373] text-sm font-medium uppercase tracking-widest mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            The Modern Open House Platform
          </p>
          <h1
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Capture Every
            <br />
            <span className="text-[#D4A373]">Lead.</span> Close More
            <br />
            <span className="text-[#D4A373]">Deals.</span>
          </h1>
          <p
            className="text-white/80 text-base md:text-lg max-w-xl mt-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Digitize your open house sign-ins, manage leads effortlessly, and
            connect with the right professionals — all from one platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 rounded-sm px-8 py-5 text-base font-medium backdrop-blur-sm"
              onClick={() => navigate('/find-jobs')}
              data-testid="hero-cta-jobs"
            >
              <Briefcase size={18} className="mr-2" /> Find Jobs
            </Button>
            <Button
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 rounded-sm px-8 py-5 text-base font-medium backdrop-blur-sm"
              onClick={() => navigate('/search')}
              data-testid="hero-cta-search"
            >
              <Search size={18} className="mr-2" /> Find Professionals
            </Button>
            <Button
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 rounded-sm px-8 py-5 text-base font-medium backdrop-blur-sm"
              onClick={() => navigate('/connect')}
              data-testid="hero-cta-connect"
            >
              <MessageSquare size={18} className="mr-2" /> Albriks Connect
            </Button>
            <Button
              className="bg-[#1A4D2E] text-white rounded-sm px-8 py-5 text-base font-semibold hover:bg-[#143D24] transition-all"
              onClick={() => navigate('/login')}
              data-testid="hero-cta-login"
            >
              Log In <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
          <p className="text-white/60 text-sm mt-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Don't have an account?{' '}
            {pagesEnabled.contact !== false ? (
              <button onClick={() => navigate('/contact')} className="text-[#D4A373] font-semibold hover:underline" data-testid="hero-contact-link">
                Contact Us
              </button>
            ) : (
              <button onClick={() => navigate('/login')} className="text-[#D4A373] font-semibold hover:underline" data-testid="hero-contact-link">
                Sign Up
              </button>
            )}
          </p>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section className="bg-[#1A4D2E] py-6" data-testid="stats-bar">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: 'Digital Sign-Ins', icon: QrCode },
            { label: 'Lead Management', icon: BarChart3 },
            { label: 'Public Profiles', icon: UserCheck },
            { label: 'Excel Export', icon: FileSpreadsheet },
          ].map(({ label, icon: Icon }) => (
            <div key={label} className="flex items-center gap-3 justify-center">
              <Icon size={22} className="text-[#D4A373]" />
              <span
                className="text-white text-sm font-medium"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-20 md:py-32" ref={howItWorksRef} data-testid="how-it-works">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16 fade-up">
            <p
              className="text-[#D4A373] text-sm font-medium uppercase tracking-widest mb-3"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Simple & Powerful
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#1A4D2E] tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              How It Works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                step: '01',
                icon: QrCode,
                title: 'Create & Share QR Code',
                desc: 'Set up your open house event in seconds. Generate a unique QR code that visitors scan to sign in digitally.',
              },
              {
                step: '02',
                icon: Smartphone,
                title: 'Visitors Sign In',
                desc: 'Guests scan the QR code on their phone, fill out a quick form, and you capture their details instantly — no paper needed.',
                highlight: 'Send a personalized thank-you email with property details attached — all in one click.',
              },
              {
                step: '03',
                icon: BarChart3,
                title: 'Manage & Convert Leads',
                desc: 'View all your leads in one dashboard. Export to Excel, assign to your team, and never lose a potential buyer again.',
              },
            ].map(({ step, icon: Icon, title, desc, highlight }) => (
              <div key={step} className="fade-up group">
                <div className="relative bg-white border border-stone-200 rounded-xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
                  <span
                    className="text-6xl font-bold text-stone-100 absolute top-4 right-6"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    {step}
                  </span>
                  <div className="w-14 h-14 rounded-lg bg-[#1A4D2E] flex items-center justify-center mb-5">
                    <Icon size={26} className="text-[#D4A373]" />
                  </div>
                  <h3
                    className="text-xl font-bold text-[#1C1917] mb-3"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    {title}
                  </h3>
                  <p
                    className="text-stone-600 text-sm leading-relaxed"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {desc}
                  </p>
                  {highlight && (
                    <div className="mt-4 flex items-start gap-2 bg-[#D4A373]/10 border border-[#D4A373]/30 rounded-lg px-4 py-3">
                      <Mail size={16} className="text-[#D4A373] mt-0.5 flex-shrink-0" />
                      <p className="text-xs font-medium text-[#1C1917] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {highlight}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES BENTO ─── */}
      <section className="py-20 md:py-32 bg-white" ref={featuresRef} data-testid="features-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16 fade-up">
            <p
              className="text-[#D4A373] text-sm font-medium uppercase tracking-widest mb-3"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Everything You Need
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#1A4D2E] tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Built for Real Estate
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Large card: QR Sign-In */}
            <div className="md:col-span-2 fade-up">
              <div className="relative rounded-xl overflow-hidden h-72 md:h-80 group">
                <img
                  src="https://images.unsplash.com/photo-1578592305066-0fb62d60309b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                  alt="QR Code Sign-In"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A4D2E] via-[#1A4D2E]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-2">
                    <QrCode size={20} className="text-[#D4A373]" />
                    <span className="text-[#D4A373] text-xs font-medium uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Digital Sign-In
                    </span>
                  </div>
                  <h3
                    className="text-2xl md:text-3xl font-bold text-white"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    QR Code Visitor Registration
                  </h3>
                  <p className="text-white/70 text-sm mt-2 max-w-md" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Replace paper sign-in sheets. Visitors scan, enter their info, and you get instant digital leads.
                  </p>
                </div>
              </div>
            </div>

            {/* Small card: Lead Dashboard */}
            <div className="fade-up">
              <div className="bg-[#1A4D2E] rounded-xl p-6 md:p-8 h-72 md:h-80 flex flex-col justify-between">
                <BarChart3 size={40} className="text-[#D4A373]" />
                <div>
                  <h3
                    className="text-xl md:text-2xl font-bold text-white mb-2"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    Real-Time Dashboard
                  </h3>
                  <p className="text-white/70 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Track visitors, open houses, and lead conversions at a glance. Export data to Excel anytime.
                  </p>
                </div>
              </div>
            </div>

            {/* Small card: Profiles */}
            <div className="fade-up">
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 md:p-8 h-64 flex flex-col justify-between">
                <UserCheck size={40} className="text-[#1A4D2E]" />
                <div>
                  <h3
                    className="text-xl font-bold text-[#1C1917] mb-2"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    Public Profiles
                  </h3>
                  <p className="text-stone-600 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Showcase your expertise with a professional profile. Collect reviews and build credibility.
                  </p>
                </div>
              </div>
            </div>

            {/* Small card: Thank You Emails */}
            <div className="fade-up">
              <div className="bg-gradient-to-br from-[#D4A373]/15 to-[#D4A373]/5 border border-[#D4A373]/30 rounded-xl p-6 md:p-8 h-64 flex flex-col justify-between">
                <div className="w-12 h-12 rounded-lg bg-[#D4A373]/20 flex items-center justify-center">
                  <Mail size={24} className="text-[#D4A373]" />
                </div>
                <div>
                  <h3
                    className="text-xl font-bold text-[#1C1917] mb-2"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    One-Click Thank You Emails
                  </h3>
                  <p className="text-stone-600 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Delight every visitor with a personalized thank-you email and property details — sent instantly after the open house.
                  </p>
                </div>
              </div>
            </div>

            {/* Large card: Professional Network */}
            <div className="md:col-span-2 fade-up">
              <div className="relative rounded-xl overflow-hidden h-64 group">
                <img
                  src="https://images.unsplash.com/photo-1758599543129-5269a8f29e68?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                  alt="Professional Network"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A4D2E] via-[#1A4D2E]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={20} className="text-[#D4A373]" />
                    <span className="text-[#D4A373] text-xs font-medium uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Network
                    </span>
                  </div>
                  <h3
                    className="text-2xl md:text-3xl font-bold text-white"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    Connect Agents & Service Providers
                  </h3>
                  <p className="text-white/70 text-sm mt-2 max-w-md" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Find attorneys, inspectors, contractors, and more. Search by state, city, and service type.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOR WHO ─── */}
      <section className="py-20 md:py-32" data-testid="for-who-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16 fade-up">
            <p
              className="text-[#D4A373] text-sm font-medium uppercase tracking-widest mb-3"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Made For You
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#1A4D2E] tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Who It's For
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Building2,
                role: 'Real Estate Agents',
                desc: 'Digitize sign-ins, capture leads, and grow your business with a professional public profile.',
                color: 'bg-[#1A4D2E]',
                iconColor: 'text-[#D4A373]',
                textColor: 'text-white',
                descColor: 'text-white/70',
              },
              {
                icon: Users,
                role: 'Brokers',
                desc: 'Manage your team of agents, oversee events, and track performance across your brokerage.',
                color: 'bg-white border border-stone-200',
                iconColor: 'text-[#1A4D2E]',
                textColor: 'text-[#1C1917]',
                descColor: 'text-stone-600',
              },
              {
                icon: Shield,
                role: 'Service Providers',
                desc: 'Get discovered by agents and home buyers. List your services and collect verified reviews.',
                color: 'bg-white border border-stone-200',
                iconColor: 'text-[#1A4D2E]',
                textColor: 'text-[#1C1917]',
                descColor: 'text-stone-600',
              },
              {
                icon: Search,
                role: 'Home Buyers',
                desc: 'Find trusted agents and service providers in your area. Sign in to open houses with ease.',
                color: 'bg-[#D4A373]/10 border border-[#D4A373]/30',
                iconColor: 'text-[#D4A373]',
                textColor: 'text-[#1C1917]',
                descColor: 'text-stone-600',
              },
            ].map(({ icon: Icon, role, desc, color, iconColor, textColor, descColor }) => (
              <div key={role} className="fade-up">
                <div className={`${color} rounded-xl p-6 md:p-8 h-full shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                  <Icon size={36} className={`${iconColor} mb-5`} />
                  <h3
                    className={`text-lg font-bold ${textColor} mb-2`}
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    {role}
                  </h3>
                  <p className={`text-sm ${descColor}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-20 md:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16 fade-up">
            <p
              className="text-[#D4A373] text-sm font-medium uppercase tracking-widest mb-3"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Trusted by Professionals
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#1A4D2E] tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              What They Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: 'Sarah Mitchell',
                role: 'Real Estate Agent',
                text: 'This platform completely changed how I run open houses. No more paper sheets — every lead is captured and organized instantly.',
                stars: 5,
              },
              {
                name: 'James Rodriguez',
                role: 'Broker, Westfield Realty',
                text: 'Managing my team of agents has never been easier. I can see every event, every lead, and every conversion from one dashboard.',
                stars: 5,
              },
              {
                name: 'Emily Chen',
                role: 'Home Inspector',
                text: 'As a service provider, getting found by agents in my area has been a game changer. The review system builds real trust.',
                stars: 5,
              },
            ].map(({ name, role, text, stars }) => (
              <div key={name} className="fade-up">
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 md:p-8 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex gap-0.5 mb-4">
                      {Array.from({ length: stars }).map((_, i) => (
                        <Star key={i} size={16} className="text-[#D4A373] fill-[#D4A373]" />
                      ))}
                    </div>
                    <p className="text-stone-700 text-sm leading-relaxed italic" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      "{text}"
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-200">
                    <p className="font-semibold text-[#1C1917] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{name}</p>
                    <p className="text-stone-500 text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-20 md:py-28 bg-[#1A4D2E] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center relative z-10">
          <h2
            className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Ready to Modernize Your
            <br />
            <span className="text-[#D4A373]">Open Houses?</span>
          </h2>
          <p
            className="text-white/70 text-base md:text-lg max-w-xl mx-auto mb-10"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Join agents and brokers who are capturing more leads, closing more deals,
            and building stronger professional networks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-[#1A4D2E] text-white rounded-sm px-10 py-5 text-base font-semibold hover:bg-[#143D24] transition-all"
              onClick={() => navigate('/login')}
              data-testid="cta-login"
            >
              Log In <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 rounded-sm px-10 py-5 text-base font-medium"
              onClick={() => navigate('/search')}
              data-testid="cta-search"
            >
              Search Professionals
            </Button>
            <Button
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 rounded-sm px-10 py-5 text-base font-medium"
              onClick={() => navigate('/connect')}
              data-testid="cta-connect"
            >
              Albriks Connect
            </Button>
            <Button
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 rounded-sm px-10 py-5 text-base font-medium"
              onClick={() => navigate('/find-jobs')}
              data-testid="cta-find-jobs"
            >
              Find Jobs
            </Button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-[#151515] py-12" data-testid="landing-footer">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="text-[#D4A373]" size={24} />
                <span
                  className="text-lg font-bold text-white"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Real Estate Lead & Referral Network
                </span>
              </div>
              <p className="text-stone-400 text-sm max-w-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                The modern platform for real estate professionals to manage open houses,
                capture leads, and grow their business.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Platform</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/login')} className="text-stone-400 hover:text-[#D4A373] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Log In
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/search')} className="text-stone-400 hover:text-[#D4A373] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Find Professionals
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/connect')} className="text-stone-400 hover:text-[#D4A373] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Albriks Connect
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Company</h4>
              <ul className="space-y-2">
                {pagesEnabled.about !== false && (
                <li>
                  <button onClick={() => navigate('/about')} className="text-stone-400 hover:text-[#D4A373] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    About
                  </button>
                </li>
                )}
                {pagesEnabled.contact !== false && (
                <li>
                  <button onClick={() => navigate('/contact')} className="text-stone-400 hover:text-[#D4A373] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Contact
                  </button>
                </li>
                )}
                {pagesEnabled.faq !== false && (
                <li>
                  <button onClick={() => navigate('/faq')} className="text-stone-400 hover:text-[#D4A373] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    FAQ
                  </button>
                </li>
                )}
                {customPages.map((pg) => (
                  <li key={pg.id}>
                    <button onClick={() => navigate(`/page/${pg.slug}`)} className="text-stone-400 hover:text-[#D4A373] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {pg.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 mt-10 pt-6 text-center">
            <p className="text-stone-500 text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>
              &copy; {new Date().getFullYear()} Real Estate Lead & Referral Network. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
